import * as Cause from "effect/Cause";
import * as Context from "effect/Context";
import * as Effect from "effect/Effect";
import * as Exit from "effect/Exit";
import * as Option from "effect/Option";
import * as Queue from "effect/Queue";
import * as Ref from "effect/Ref";
import type * as Scope from "effect/Scope";
import * as SubscriptionRef from "effect/SubscriptionRef";

import type { ConnectionCatalogEntry } from "./catalog.ts";
import * as ConnectionDriver from "./driver.ts";
import {
  type ConnectionAttemptError,
  type ConnectionAttemptStage,
  ConnectionTransientError,
  type PreparedConnection,
  type PrimaryConnectionTarget,
  type SupervisorConnectionState,
} from "./model.ts";
import * as RpcSession from "../rpc/session.ts";
import { safeErrorLogAttributes } from "../errors/safeLog.ts";

// The runtime is a worker in this page: a cold start downloads and opens its database,
// which a slow device or network can stretch, but nothing remote stands behind it.
const CONNECTION_ESTABLISHMENT_TIMEOUT = "60 seconds";
// An attempt that never connected waits this long before the next fresh runtime, so a
// runtime that cannot start does not respawn in a tight loop.
const RESTART_PAUSE = "1 second";

type SupervisorSignal =
  | { readonly _tag: "ConnectRequested" }
  | { readonly _tag: "DisconnectRequested" }
  | { readonly _tag: "RetryRequested" };

type AttemptOutcome =
  | { readonly _tag: "Interrupted" }
  | {
      readonly _tag: "Failure";
      readonly established: boolean;
      readonly failure: ConnectionAttemptError;
    };

function annotateTarget(target: PrimaryConnectionTarget) {
  return Effect.annotateCurrentSpan({
    "environment.id": target.environmentId,
    "environment.label": target.label,
  });
}

function failureFromCause(
  target: PrimaryConnectionTarget,
  cause: Cause.Cause<ConnectionAttemptError>,
  established: boolean,
): AttemptOutcome {
  if (Cause.hasInterruptsOnly(cause)) return { _tag: "Interrupted" };
  const typedFailure = cause.reasons.find(Cause.isFailReason);
  return {
    _tag: "Failure",
    established,
    failure:
      typedFailure?.error ??
      new ConnectionTransientError({
        reason: "transport",
        detail: `${target.label} connection failed unexpectedly.`,
      }),
  };
}

export class EnvironmentSupervisor extends Context.Service<
  EnvironmentSupervisor,
  {
    readonly target: PrimaryConnectionTarget;
    readonly state: SubscriptionRef.SubscriptionRef<SupervisorConnectionState>;
    readonly session: SubscriptionRef.SubscriptionRef<Option.Option<RpcSession.RpcSession>>;
    readonly prepared: SubscriptionRef.SubscriptionRef<Option.Option<PreparedConnection>>;
    readonly connect: Effect.Effect<void>;
    readonly disconnect: Effect.Effect<void>;
    readonly retryNow: Effect.Effect<void>;
  }
>()("@heydex/client-runtime/connection/supervisor/EnvironmentSupervisor") {}

/**
 * Keeps one session with the page's runtime while one is wanted. A session that ends
 * is replaced at once by a fresh runtime; only a blocked failure waits for a retry.
 */
export const make = Effect.fn("EnvironmentSupervisor.make")(function* (
  entry: ConnectionCatalogEntry,
): Effect.fn.Return<
  EnvironmentSupervisor["Service"],
  never,
  ConnectionDriver.ConnectionDriver | Scope.Scope
> {
  const target = entry.target;
  yield* annotateTarget(target);

  const driver = yield* ConnectionDriver.ConnectionDriver;
  const desired = yield* Ref.make(false);
  const signals = yield* Queue.unbounded<SupervisorSignal>();
  const state = yield* SubscriptionRef.make<SupervisorConnectionState>({
    desired: false,
    phase: "available",
    stage: null,
    attempt: 0,
    generation: 0,
    lastFailure: null,
  });
  const session = yield* SubscriptionRef.make<Option.Option<RpcSession.RpcSession>>(Option.none());
  const prepared = yield* SubscriptionRef.make<Option.Option<PreparedConnection>>(Option.none());
  const clearLease = Effect.all(
    [SubscriptionRef.set(session, Option.none()), SubscriptionRef.set(prepared, Option.none())],
    { discard: true },
  );

  const signal = (next: SupervisorSignal) => Queue.offer(signals, next);

  const connectingState = (
    attempt: number,
    generation: number,
    lastFailure: ConnectionAttemptError | null,
    stage: ConnectionAttemptStage,
  ): SupervisorConnectionState => ({
    desired: true,
    phase: "connecting",
    stage,
    attempt,
    generation,
    lastFailure,
  });

  // Ends when the session should go: an explicit disconnect.
  const waitForDisconnect = Effect.gen(function* () {
    for (;;) {
      const next = yield* Queue.take(signals);
      if (next._tag === "DisconnectRequested") return;
    }
  });

  const runAttempt = Effect.fnUntraced(function* (
    attempt: number,
    generation: number,
    lastFailure: ConnectionAttemptError | null,
  ) {
    const establishment = yield* driver
      .connect(entry, (stage) =>
        SubscriptionRef.set(state, connectingState(attempt, generation, lastFailure, stage)),
      )
      .pipe(
        Effect.timeoutOrElse({
          duration: CONNECTION_ESTABLISHMENT_TIMEOUT,
          orElse: () =>
            Effect.fail(
              new ConnectionTransientError({
                reason: "timeout",
                detail: `${target.label} did not start.`,
              }),
            ),
        }),
        Effect.raceFirst(waitForDisconnect.pipe(Effect.andThen(Effect.interrupt))),
        Effect.exit,
      );
    if (Exit.isFailure(establishment)) {
      const defect = establishment.cause.reasons.find(Cause.isDieReason)?.defect;
      if (defect !== undefined) {
        yield* Effect.logError("Connection attempt failed with an unexpected defect.").pipe(
          Effect.annotateLogs({
            "environment.id": target.environmentId,
            ...safeErrorLogAttributes(defect),
          }),
        );
      }
      return failureFromCause(target, establishment.cause, false);
    }

    const lease = establishment.value;
    if (!(yield* Ref.get(desired))) return { _tag: "Interrupted" } satisfies AttemptOutcome;
    yield* SubscriptionRef.set(prepared, Option.some(lease.prepared));
    yield* SubscriptionRef.set(session, Option.some(lease.session));
    yield* SubscriptionRef.set(state, {
      desired: true,
      phase: "connected",
      stage: null,
      attempt,
      generation,
      lastFailure: null,
    });

    const connectedExit = yield* Effect.raceFirst(lease.session.closed, waitForDisconnect).pipe(
      Effect.exit,
    );
    if (Exit.isSuccess(connectedExit)) return { _tag: "Interrupted" } satisfies AttemptOutcome;
    const outcome = failureFromCause(target, connectedExit.cause, true);
    if (outcome._tag === "Failure") {
      yield* Effect.void.pipe(
        Effect.withSpan("EnvironmentSupervisor.connectionLost", {
          root: true,
          attributes: {
            "environment.id": target.environmentId,
            "connection.failure.reason": outcome.failure.reason,
            "connection.failure.detail": outcome.failure.detail,
          },
        }),
      );
    }
    return outcome;
  }, Effect.ensuring(clearLease));

  // Waits for a signal that asks for another attempt.
  const waitForConnectSignal = Effect.gen(function* () {
    for (;;) {
      const next = yield* Queue.take(signals);
      if (next._tag !== "DisconnectRequested") return;
    }
  });

  const run = Effect.fnUntraced(function* () {
    let attempt = 0;
    let generation = 0;
    let lastFailure: ConnectionAttemptError | null = null;
    for (;;) {
      if (!(yield* Ref.get(desired))) {
        attempt = 0;
        lastFailure = null;
        yield* clearLease;
        yield* SubscriptionRef.set(state, {
          desired: false,
          phase: "available",
          stage: null,
          attempt: 0,
          generation,
          lastFailure: null,
        });
        yield* Queue.take(signals);
        continue;
      }

      // Signals sent before this attempt are answered by it.
      yield* Queue.clear(signals);
      attempt += 1;
      generation += 1;
      yield* SubscriptionRef.set(
        state,
        connectingState(attempt, generation, lastFailure, "opening"),
      );
      const outcome: AttemptOutcome = yield* Effect.scoped(
        runAttempt(attempt, generation, lastFailure),
      );
      if (outcome._tag === "Interrupted") continue;

      lastFailure = outcome.failure;
      if (outcome.established) attempt = 0;
      if (outcome.failure._tag === "ConnectionBlockedError") {
        yield* SubscriptionRef.set(state, {
          desired: yield* Ref.get(desired),
          phase: "blocked",
          stage: null,
          attempt,
          generation,
          lastFailure,
        });
        yield* waitForConnectSignal;
        attempt = 0;
        continue;
      }
      yield* SubscriptionRef.set(
        state,
        connectingState(attempt, generation, lastFailure, "opening"),
      );
      if (!outcome.established) {
        yield* Effect.raceFirst(Effect.sleep(RESTART_PAUSE), Queue.take(signals));
      }
    }
  });

  yield* run().pipe(Effect.forkScoped);

  const connect = Ref.set(desired, true).pipe(
    Effect.andThen(signal({ _tag: "ConnectRequested" })),
    Effect.asVoid,
    Effect.withSpan("EnvironmentSupervisor.connect"),
  );

  const disconnect = Ref.set(desired, false).pipe(
    Effect.andThen(signal({ _tag: "DisconnectRequested" })),
    Effect.asVoid,
    Effect.withSpan("EnvironmentSupervisor.disconnect"),
  );

  const retryNow = signal({ _tag: "RetryRequested" }).pipe(
    Effect.asVoid,
    Effect.withSpan("EnvironmentSupervisor.retryNow"),
  );

  yield* Effect.addFinalizer(() => Queue.shutdown(signals).pipe(Effect.andThen(clearLease)));

  return EnvironmentSupervisor.of({
    target,
    state,
    session,
    prepared,
    connect,
    disconnect,
    retryNow,
  });
});
